/*!
  Pioneer WFRIA Masthead v4.0.0-beta.53
  © 2023 Wells Fargo. All rights reserved.
*/
try {
  if (!window.__PIONEER_GLOBAL__) {
    window.__PIONEER_GLOBAL__ = {
      version: "4.0.0-beta.53",
      components: ["Masthead"]
    };
  } else if (window.__PIONEER_GLOBAL__.components && window.__PIONEER_GLOBAL__.components.indexOf("Masthead") < 0) {
    window.__PIONEER_GLOBAL__.components.push("Masthead");
  }
} catch (error) {
  // catching server side rendering issues - windows is not defined there!
}

import _defineProperty from "@babel/runtime/helpers/defineProperty";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import { useTheme } from "@emotion/react";
import PropTypes from "prop-types";
import React, { useEffect, useRef } from "react";
import { ZINDEX } from "../common/constants";
import extractTheme from "./extractTheme";
import STRINGS from "./strings";
/**
 * The Masthead is the header area at the top of each application screen. It
 * is also used at the top of Panels and child windows. It displays a logo
 * and may also serve as a container for application-wide tools such as search,
 * notifications, and access to user settings.
 */

import { jsx as _jsx } from "@emotion/react/jsx-runtime";
import { jsxs as _jsxs } from "@emotion/react/jsx-runtime";

function Masthead(_ref) {
  let {
    children,
    customStrings,
    elementToFocusOnSkip,
    homeUrl,
    logo
  } = _ref;
  const theme = useTheme();
  const {
    cssRoot,
    cssBody,
    cssLogo,
    cssToolsContainer,
    cssLogoLink,
    cssSkipLink
  } = extractTheme({
    theme
  });
  cssSkipLink.zIndex = ZINDEX.SKIP_LINK;

  const componentStrings = _objectSpread(_objectSpread({}, STRINGS), customStrings);

  const elementToFocusOnSkipRef = useRef();
  useEffect(() => {
    if (elementToFocusOnSkip) {
      if (typeof elementToFocusOnSkip === "string") {
        elementToFocusOnSkipRef.current = document.querySelector(elementToFocusOnSkip);
      } else {
        elementToFocusOnSkipRef.current = elementToFocusOnSkip();
      }

      if (elementToFocusOnSkipRef.current) {
        elementToFocusOnSkipRef.current.setAttribute("tabindex", -1);
        elementToFocusOnSkipRef.current.setAttribute("aria-label", "content");
      }
    }
  }, [elementToFocusOnSkip]);
  /**
   * Handles the click event of the 'Skip to content' link.
   * Sets focus on the element provided by elementToFocusOnSkip.
   */

  const handleSkipLinkClick = e => {
    e.preventDefault();
    elementToFocusOnSkipRef.current.focus();
  };
  /**
   * As per specs, <img> tag should be used to display header logo.
   * We first load header logo with `1x1px-transparent-gif` followed by the actual image
   * as background url in css.
   * This is to avoid api-breaking changes while updating from <span> to <img> usage.
   * Also, if we give empty src to the <img> tag, unwanted borders are seen around the image.
   * Source of CSS hack is https://css-tricks.com/snippets/html/base64-encode-of-1x1px-transparent-gif/
   */


  const logoImage = logo || _jsx("img", {
    alt: componentStrings.logoAriaLabel,
    css: cssLogo,
    className: "pnr-masthead__logo",
    src: "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7"
  });

  return _jsxs(React.Fragment, {
    children: [elementToFocusOnSkip && // eslint-disable-next-line jsx-a11y/anchor-is-valid
    _jsx("a", {
      href: "",
      css: cssSkipLink,
      onClick: handleSkipLinkClick,
      className: "wf-u-visually-hidden wf-u-visually-hidden-focusable pnr-masthead__skip-link",
      children: componentStrings.skipLabel
    }), _jsx("header", {
      css: cssRoot,
      children: _jsxs("div", {
        className: "pnr-masthead__body",
        css: cssBody,
        children: [homeUrl ? _jsx("a", {
          href: homeUrl,
          className: "pnr-masthead__logo-link",
          css: cssLogoLink,
          children: logoImage
        }) : logoImage, _jsx("div", {
          className: "prn-masthead__tools",
          css: cssToolsContainer,
          children: children
        })]
      })
    })]
  });
}

Masthead.propTypes = process.env.NODE_ENV !== "production" ? {
  /** Tools to be added to the Masthead */
  children: PropTypes.node,

  /**
   * Override for strings used within the component.
   *  - `logoAriaLabel`: aria-label for the Masthead logo.
   *     Default to "Wells Fargo Logo".
   *  - `skipLabel`: The text of the Skip Link.
   *     Defaults to "Skip to content"
   */
  customStrings: PropTypes.shape({
    logoAriaLabel: PropTypes.string,
    skipLabel: PropTypes.string
  }),

  /**
   * Element that should be focused on the click of the Skip Link. CSS selector
   * or function that returns the element node. If not provided, no Skip Link
   * will be rendered.
   */
  elementToFocusOnSkip: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),

  /** href for the logo link. If provided, the logo will be wrapped in a link. */
  homeUrl: PropTypes.string,

  /**
   * Allowing applications to provide their own logo. This element will be
   * wrapped in link if homeUrl is provided.
   * logo prop.
   */
  logo: PropTypes.element
} : {};
Masthead.defaultProps = {
  children: null,
  customStrings: null,
  elementToFocusOnSkip: null,
  homeUrl: null,
  logo: null
};
export default Masthead;